import { z } from 'zod';
import { IsoTime, Labels } from './basics.js';
import { ServerGroupRef, ServerSummary } from './server.js';
/**
 * Enrollment tokens as the dashboard manages them (knowledge doc section 10,
 * method A; section 15).
 *
 * The token itself appears in exactly one place: the response to creating it.
 * Every other shape carries the row without it, because the API stores only
 * `sha256(token)` and could not show it again even if a route wanted to.
 */
/** Section 12 rates managing these High risk: admin, with a password or 2FA re-check. */
export const EnrollmentTokenId = z.uuid();
/** Auto-approve, or wait for an admin to let the server in. */
export const ApprovalMode = z.enum(['auto_approve', 'needs_approval']);
export const EnrollmentTokenSummary = z.object({
    id: EnrollmentTokenId,
    expiresAt: IsoTime,
    maxUses: z.number().int().positive(),
    usedCount: z.number().int().nonnegative(),
    group: ServerGroupRef.nullable(),
    labels: Labels,
    approvalMode: ApprovalMode,
    revokedAt: IsoTime.nullable(),
    createdAt: IsoTime,
    /** The address of whoever created it, or null once that account is removed. */
    createdByEmail: z.email().nullable(),
    /**
     * Derived rather than stored, because "expired" is a fact about the clock and a
     * column would have to be swept to stay true.
     */
    state: z.enum(['active', 'exhausted', 'expired', 'revoked']),
});
/**
 * Bounds on what a token may be created with.
 *
 * Section 10 gives no numbers, so these are ours: a month is long enough to
 * provision a fleet and short enough that a forgotten token stops working, and a
 * thousand uses is far past any real batch while still refusing "unlimited" —
 * which is the shape of a token nobody ever revokes.
 */
export const ENROLLMENT_TOKEN_MAX_TTL_DAYS = 90;
export const ENROLLMENT_TOKEN_DEFAULT_TTL_DAYS = 30;
export const ENROLLMENT_TOKEN_MAX_USES = 1000;
export const CreateEnrollmentTokenRequest = z.object({
    expiresInDays: z
        .number()
        .int()
        .min(1)
        .max(ENROLLMENT_TOKEN_MAX_TTL_DAYS)
        .default(ENROLLMENT_TOKEN_DEFAULT_TTL_DAYS),
    maxUses: z.number().int().min(1).max(ENROLLMENT_TOKEN_MAX_USES).default(1),
    groupId: z.uuid().nullable().default(null),
    labels: Labels.default({}),
    /**
     * Defaults to the cautious one. A token that auto-approves is a token that lets
     * anyone holding it put a server into the fleet with nobody looking.
     */
    approvalMode: ApprovalMode.default('needs_approval'),
});
/**
 * The one response that carries the plaintext. Section 10: "The token is shown
 * **once**; the API stores only its hash."
 */
export const CreatedEnrollmentToken = z.object({
    token: z.string().min(20).max(256),
    summary: EnrollmentTokenSummary,
});
/** Section 10: "It can be revoked, and React lists servers that used it." */
export const EnrollmentTokenDetail = z.object({
    summary: EnrollmentTokenSummary,
    servers: z.array(ServerSummary).max(200),
});
export const EnrollmentTokenListQuery = z.object({
    /** Hides revoked, expired and exhausted ones, which is the usual view. */
    active_only: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().max(512).optional(),
});
//# sourceMappingURL=enrollment-token.js.map